// One building-record section as a form (prototype renderSection / fieldHTML): every field by its type,
// the section's table (add row, standard rows, remove), and the section status. Secrets show as dots
// with a peek button; a masked value the caller cannot see is left alone on save.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, Lock, Plus, Trash2 } from 'lucide-react';
import { Button, Field, Input, Select, Textarea, Skeleton } from '../ui';
import { FormError } from '../forms/common';
import { buildingApi, type FieldDef, type SectionStatus, type SectionView } from '../../lib/building';
import { formatDate } from '../../lib/viz';
import { Drawer, DueChip, StatusChip, Ring } from './bits';
import { STATUS_COLOUR } from '../../lib/building';
import { PhoneInput } from '../ui/PhoneInput';

type Vals = Record<string, string>;
const toStr = (v: unknown) => (v === undefined || v === null ? '' : String(v));
const inputType = (f: FieldDef) => (f.base === 'date' ? 'date' : f.base === 'email' ? 'email' : f.base === 'num' ? 'number' : 'text');

function Control({ f, value, onChange, disabled, compact, mask }: { f: FieldDef; value: string; onChange: (v: string) => void; disabled: boolean; compact?: boolean; mask?: string }) {
  const [peek, setPeek] = useState(false);
  const cls = compact ? '!rounded-lg !px-2 !py-1.5 !text-[12.5px] w-full' : 'w-full';
  if (f.base === 'area') return <Textarea className={`${cls} min-h-[72px]`} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder="—" />;
  if (f.base === 'select') return (
    <Select className={cls} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      <option value="">—</option>{f.options?.map((o) => <option key={o} value={o}>{o}</option>)}
    </Select>
  );
  if (f.base === 'secret') {
    const masked = !!mask && value === mask;
    return (
      <div className="relative">
        <Input className={`${cls} pr-10`} type={peek && !masked ? 'text' : 'password'} value={value} autoComplete="new-password" placeholder="—"
          onFocus={(e) => { if (masked && !disabled) e.currentTarget.select(); }} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
        {!masked && <button type="button" onClick={() => setPeek((p) => !p)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-ink-400 hover:text-ink-800" aria-label={peek ? 'Hide' : 'Show'}>{peek ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>}
      </div>
    );
  }
  if (f.base === 'tel') return <PhoneInput className={compact ? '!rounded-lg !py-1.5 !text-[12.5px]' : ''} autoComplete="off" value={value} onChange={onChange} disabled={disabled} />;
  return <Input className={cls} type={inputType(f)} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} placeholder="—" step={f.base === 'num' ? 'any' : undefined} />;
}

export function SectionDrawer({ propertyId, sectionId, onClose, onSaved, statuses, prefill }: { propertyId: string; sectionId: string; onClose: () => void; onSaved: (v: SectionView) => void; statuses: Array<{ key: SectionStatus; label: string }>; /** Values to start with on top of what is saved (the calendar's + hands a date in). */ prefill?: Record<string, string> }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['building-section', propertyId, sectionId], queryFn: () => buildingApi.section(propertyId, sectionId) });
  const v = q.data;
  const [fields, setFields] = useState<Vals>({});
  const [rows, setRows] = useState<Vals[]>([]);
  const [status, setStatus] = useState<SectionStatus | ''>('');
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!v) return;
    setFields({ ...Object.fromEntries(v.section.fields.map((f) => [f.key, toStr(v.values.fields[f.key])])), ...(prefill ?? {}) });
    setRows(v.values.rows.map((r) => Object.fromEntries(Object.entries(r).map(([k, x]) => [k, toStr(x)]))));
    setStatus(['NOT_STARTED', 'IN_PROGRESS'].includes(v.status) ? '' : v.status);
    setDirty(!!prefill && Object.keys(prefill).length > 0);
  // `prefill` is applied once, when the section loads; a later change must not wipe what the user typed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v]);
  const readOnly = !v?.canEdit;
  const mask = '••••••';

  const save = useMutation({
    mutationFn: () => {
      const changed: Record<string, unknown> = {};
      for (const f of v!.section.fields) if (toStr(v!.values.fields[f.key]) !== (fields[f.key] ?? '')) changed[f.key] = fields[f.key] ?? '';
      const autoNow = ['NOT_STARTED', 'IN_PROGRESS'].includes(v!.status);
      const statusBody = status === '' ? (autoNow ? undefined : null) : status === v!.status ? undefined : status;
      return buildingApi.saveSection(propertyId, sectionId, {
        fields: Object.keys(changed).length ? changed : undefined,
        rows: v!.section.table ? rows : undefined,
        ...(statusBody !== undefined ? { status: statusBody } : {}),
      });
    },
    onSuccess: (saved) => {
      qc.setQueryData(['building-section', propertyId, sectionId], saved);
      void qc.invalidateQueries({ queryKey: ['building-record', propertyId] });
      void qc.invalidateQueries({ queryKey: ['key-dates', propertyId] });
      void qc.invalidateQueries({ queryKey: ['who-to-call', propertyId] });
      onSaved(saved);
    },
  });

  const set = (k: string, x: string) => { setFields((s) => ({ ...s, [k]: x })); setDirty(true); };
  const setCell = (i: number, k: string, x: string) => { setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: x } : r))); setDirty(true); };
  const seedRows = () => {
    const t = v!.section.table!;
    const have = new Set(rows.map((r) => (r[t.seedKey!] ?? '').toLowerCase()));
    setRows((rs) => [...rs, ...t.seed!.filter((s) => !have.has(s.toLowerCase())).map((s) => ({ [t.seedKey!]: s }))]);
    setDirty(true);
  };
  const errors = useMemo(() => {
    const d = (save.error as { details?: { fieldErrors?: Record<string, string> } } | null)?.details;
    return d?.fieldErrors ?? {};
  }, [save.error]);

  return (
    <Drawer onClose={onClose} accent={v ? STATUS_COLOUR[v.effectiveStatus] : undefined}
      title={v ? <span className="flex items-center gap-3"><Ring pct={v.progress.pct} size={40} />{v.section.n}. {v.section.title}</span> : <Skeleton className="h-4 w-44" />}
      subtitle={v && <span className="flex flex-wrap items-center gap-2">{v.section.description}</span>}
      footer={v && (readOnly
        ? <span className="mr-auto flex items-center gap-1.5 text-[12px] text-ink-500"><Lock className="h-3.5 w-3.5" /> Read only{v.secretsMasked ? ' — codes are hidden' : ''}</span>
        : <>
          {save.isSuccess && !dirty && <span className="mr-auto text-[12px] text-emerald-700">Saved</span>}
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save section'}</Button>
        </>)}>
      {q.isLoading && <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-xl bg-ink-100" />)}</div>}
      {q.error && <FormError error={q.error} />}
      {v && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusChip status={v.effectiveStatus} label={v.effectiveStatusLabel} />
            <span className="text-[12px] text-ink-500">{v.progress.filled} of {v.progress.total} filled</span>
            {v.updatedAt && <span className="text-[12px] text-ink-400">· updated {formatDate(v.updatedAt)}{v.updatedByName ? ` by ${v.updatedByName}` : ''}</span>}
          </div>
          {v.dueDates.length > 0 && (
            <ul className="space-y-1 rounded-xl border border-ink-100 bg-ink-50/60 px-3 py-2">
              {v.dueDates.map((d, i) => <li key={i} className="flex flex-wrap items-center justify-between gap-2 text-[12.5px]"><span className="text-ink-700">{d.label} · {formatDate(d.date)}</span><DueChip due={d.due} /></li>)}
            </ul>
          )}
          {!readOnly && (
            <Field label="Section status" hint="Leave on automatic and it follows how much is filled in. A date in this section that has passed shows it as Overdue whatever is chosen here.">
              <Select value={status} onChange={(e) => { setStatus(e.target.value as SectionStatus | ''); setDirty(true); }} className="w-full sm:w-64">
                <option value="">Automatic ({v.progress.filled ? 'In progress' : 'Not started'})</option>
                {statuses.filter((s) => !['NOT_STARTED', 'IN_PROGRESS'].includes(s.key)).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </Select>
            </Field>
          )}
          {v.section.fields.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              {v.section.fields.map((f) => (
                <div key={f.key} className={f.wide ? 'sm:col-span-2' : ''}>
                  <label className="block">
                    <span className="mb-1.5 flex items-center gap-2 text-[12px] font-medium text-ink-600">{f.label}{f.base === 'secret' && <span className="rounded bg-amber-50 px-1.5 py-[1px] text-[10px] font-semibold uppercase tracking-wider text-amber-700">sensitive</span>}</span>
                    <Control f={f} value={fields[f.key] ?? ''} onChange={(x) => set(f.key, x)} disabled={readOnly} mask={mask} />
                  </label>
                  {errors[f.key] && <p className="mt-1 text-[11.5px] text-red-600">{errors[f.key]}</p>}
                </div>
              ))}
            </div>
          )}
          {v.section.table && (
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-[13px] font-semibold text-ink-900">{v.section.table.title} <span className="font-normal text-ink-400">({rows.length})</span></h4>
                {!readOnly && <div className="flex flex-wrap gap-1.5">
                  {v.section.table.seed && <Button size="sm" variant="secondary" onClick={seedRows}>{v.section.table.seedLabel ?? 'Add standard rows'}</Button>}
                  <Button size="sm" variant="secondary" onClick={() => { setRows((rs) => [...rs, {}]); setDirty(true); }}><Plus className="h-3.5 w-3.5" /> Add row</Button>
                </div>}
              </div>
              {rows.length === 0 ? <p className="rounded-xl border border-ink-200 py-6 text-center text-[12px] text-ink-400">No rows yet</p> : (
                <div className="space-y-2">
                  {rows.map((r, i) => (
                    <div key={i} className="rounded-xl border border-ink-100 p-2.5">
                      <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-2 lg:grid-cols-3">
                        {v.section.table!.cols.map((c) => (
                          <label key={c.key} className="block min-w-0">
                            <span className="mb-0.5 block text-[10.5px] font-medium uppercase tracking-wider text-ink-400">{c.label}</span>
                            <Control f={c} compact value={r[c.key] ?? ''} onChange={(x) => setCell(i, c.key, x)} disabled={readOnly} />
                            {errors[`rows[${i}].${c.key}`] && <span className="text-[11px] text-red-600">{errors[`rows[${i}].${c.key}`]}</span>}
                          </label>
                        ))}
                      </div>
                      {!readOnly && <div className="mt-1.5 text-right"><button onClick={() => { setRows((rs) => rs.filter((_, j) => j !== i)); setDirty(true); }} className="inline-flex items-center gap-1 text-[11.5px] text-ink-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /> Remove</button></div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {v.section.map && <p className="rounded-xl bg-ink-50 px-3 py-2 text-[12px] text-ink-500">The car-park plan for this section arrives with Parking (Task I).</p>}
          {save.error && !Object.keys(errors).length && <FormError error={save.error} />}
          {save.error && Object.keys(errors).length > 0 && <p className="text-[12px] text-red-600">Some values are not right — see the fields marked above.</p>}
        </div>
      )}
    </Drawer>
  );
}
